import {
  BaseImageInterstitial,
  BaseImageOrGraphType,
} from '@/app/components/questionnaireV2/components/survey-item/components/interstitial/base-image-interstitial';

import { DTORiskFactor, Frame } from '@/api/generated-catch-backend/model';
import { AnimatePresence, motion } from 'motion/react';
import { RecommendedActionCard } from '../dashboard/components/dashboard-body/components/recommended-actions/components/recommended-action-card';
import { interventionHasEssentialData } from '../dashboard/components/dashboard-body/components/recommended-actions/recommended-actions';
import { RiskFactorCard } from '../dashboard/components/dashboard-body/components/risk-factors/components/risk-factor-card';
import { TestSuggestionCard } from '../dashboard/components/dashboard-body/components/testing-plan/components/testing-suggestion-card';
import { TopRisksCard } from '../dashboard/components/dashboard-body/components/top-3-risks/components/top-risks-card';
import RiskGauge from '../dashboard/components/dashboard-header-v2/components/risk-gauge';
import RiskStatusBadge from '../dashboard/components/dashboard-header-v2/components/risk-status-badge';
import NextArrow from '../questionnaireV2/icons/next-arrow';
import { EmergingRiskFactor } from './components/emgering-risk-factor';

type FrameWithGraph = Frame & { imageOrGraph: Required<Pick<Frame, 'imageOrGraph'>> };
const hasImageOrGraph = (frame: Frame): frame is FrameWithGraph => !!frame.imageOrGraph;

interface RiskScoreRevealFrameProps {
  frame: Frame;
  onNext?: () => void;
  buttonText?: string;
  className?: string;
  'data-testid'?: string;
}

const isRiskDecreasing = (factor: DTORiskFactor) =>
  factor.impact.startsWith('-') || factor.riskFactorType === 'non-quantifiable-negative';

// Helper function to convert risk score reveal graphic type to base type
const convertRiskScoreGraphicToBase = (frame: Frame): BaseImageOrGraphType | null => {
  if (!hasImageOrGraph(frame)) return null;
  const { imageOrGraph: graphic } = frame;
  return {
    type: graphic.type,
    asset: graphic.asset,
    height: graphic.height,
    width: graphic.width,
    customGraphicName: graphic.customGraphicName,
  };
};

const renderCustomGraphic =
  ({ frame }: { frame: Frame }) =>
  (): React.ReactNode => {
    if (!hasImageOrGraph(frame)) return null;
    if (frame.imageOrGraph.customGraphicName === 'speedometer' && frame.data.type === 'risk-scores') {
      return (
        <div className="order-2 flex flex-shrink-0 flex-col items-center gap-6 lg:order-1 lg:items-center">
          <RiskGauge
            riskPercentage={frame.data.riskScores.overall.lifetimeRisk}
            populationAverage={frame.data.riskScores.overall.populationAverage}
          />
          <RiskStatusBadge
            riskLevel={frame.data.riskScores.overall.benchmark}
            populationAverage={frame.data.riskScores.overall.populationAverage}
          />
        </div>
      );
    } else if (frame.imageOrGraph.customGraphicName === 'risk-scores' && frame.data.type === 'risk-scores') {
      return (
        // Design has a cool accordion reveal animation but we'll keep this simple
        <AnimatePresence mode="wait">
          <motion.div
            className="column flex gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <div className="order-2 flex flex-shrink-0 flex-col items-center gap-6 lg:order-1 lg:items-center">
              {frame.data.riskScores.topDiseasesByRelativeRisk.slice(0, 3).map((risk) => (
                <TopRisksCard
                  key={risk.disease}
                  diseaseSlug={risk.diseaseSlug}
                  riskValue={risk.lifetimeRisk}
                  disease={risk.disease}
                  benchmark={risk.benchmark}
                  populationAverage={risk.populationAverage}
                  testId="top-cancer-card"
                  badgePosition="inline"
                  variant="fixed"
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      );
    } else if (frame.imageOrGraph.customGraphicName === 'risk-factors' && frame.data.type === 'risk-factors') {
      return (
        <AnimatePresence mode="wait">
          <motion.div
            className="column flex gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <div
              className="w-full max-w-[calc(100vw-2rem)] overflow-x-auto md:max-w-xl lg:max-w-2xl [&::-webkit-scrollbar]:hidden"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              <div className="flex w-max gap-3 px-4">
                {frame.data.riskFactors.riskFactors.filter(isRiskDecreasing).map((factor, index) => (
                  <div key={factor.name || index} className="flex-shrink-0">
                    <RiskFactorCard
                      disableClick
                      riskFactor={factor}
                      type={factor.impact.startsWith('-') ? 'decreasing' : 'increasing'}
                      text={[
                        {
                          _type: 'block',
                          _key: 'd8c95b26bf2a',
                          style: 'normal',
                          markDefs: [],
                          children: [
                            {
                              _type: 'span',
                              _key: 'f76d2c60f3b9',
                              text: factor.shortDescription,
                              marks: [],
                            },
                          ],
                        },
                      ]}
                    />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      );
    } else if (frame.imageOrGraph.customGraphicName === 'test-suggestions' && frame.data.type === 'test-suggestions') {
      return (
        <AnimatePresence mode="wait">
          <motion.div
            className="column flex gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <div
              className="w-full max-w-[calc(100vw-2rem)] overflow-x-auto md:max-w-xl lg:max-w-2xl [&::-webkit-scrollbar]:hidden"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              <div className="flex w-max gap-3 px-4">
                {frame.data.testSuggestions.data.map((testSuggestion) => (
                  <TestSuggestionCard test={testSuggestion} key={testSuggestion.slug} />
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      );
    } else if (frame.imageOrGraph.customGraphicName === 'action-plan' && frame.data.type === 'action-plan') {
      return (
        // Design has a cool accordion reveal animation but we'll keep this simple
        <AnimatePresence mode="wait">
          <motion.div
            className="column flex gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <div className="order-2 flex flex-shrink-0 flex-col items-center gap-6 lg:order-1 lg:items-center">
              {frame.data.actionPlan.data
                .filter(interventionHasEssentialData)
                .slice(0, 3)
                .map((action) => (
                  <RecommendedActionCard key={action.id} action={action} />
                ))}
            </div>
          </motion.div>
        </AnimatePresence>
      );
    } else if (
      frame.imageOrGraph.customGraphicName === 'emerging-risk-factor' &&
      frame.data.type === 'emerging-risk-factor'
    ) {
      return (
        <EmergingRiskFactor
          riskFactorName={frame.data.emergingRiskFactor.riskFactorName}
          riskFactorSubtext={frame.data.emergingRiskFactor.riskFactorSubtext}
        />
      );
    } else {
      console.error(
        `Unable to display custom graphic for customGraphicName=${frame.imageOrGraph.customGraphicName} and data=${frame.data.type}`,
      );
    }
  };

export const RiskScoreRevealFrame = ({
  frame,
  onNext,
  buttonText = 'Continue',
  className = '',
  'data-testid': testId,
}: RiskScoreRevealFrameProps) => {
  // Convert background opacity from string to number
  const backgroundOpacity = frame.backgroundOpacity ? parseFloat(frame.backgroundOpacity) : 0.6;

  return (
    <BaseImageInterstitial
      // Risk score reveal typically doesn't have title above or below, just subtitle below
      titleAbove={null}
      subtitleAbove={null}
      titleBelow={null}
      subtitleBelow={frame.subtitleBelow}
      primaryGraphic={convertRiskScoreGraphicToBase(frame)}
      secondaryGraphic={null} // Risk score reveal typically uses single graphic per frame
      backgroundAsset={frame.backgroundAsset}
      backgroundOpacity={backgroundOpacity}
      onNext={onNext}
      buttonText={buttonText}
      className={`${className} [&_.text-center]:text-left`}
      renderButton={() => (
        <button type="button" onClick={onNext} className={'btn-primary'}>
          <span>{buttonText}</span>
          <NextArrow />
        </button>
      )}
      renderCustomGraphic={renderCustomGraphic({ frame })}
      data-testid={testId}
    />
  );
};
